interface RateMeterProps {
  label: string
  /** Current rate, as a percent (e.g. 4.2 means 4.2%). */
  value: number
  /** The breaker's max allowed rate, as a percent. At/over this reads as alarming. */
  max: number
}

/**
 * A breaker-rate readout: the current rate against its threshold, with a bar that fills toward the
 * limit and turns red once the rate reaches or exceeds it. Used for hard-bounce and complaint rates.
 */
export function RateMeter({ label, value, max }: RateMeterProps) {
  const over = max > 0 && value >= max
  // Bar fills relative to the threshold; clamp so an over-limit rate pins (and reds) the bar.
  const fillPercent = max > 0 ? Math.min((value / max) * 100, 100) : 0

  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{label}</span>
        <span
          className={
            'text-sm font-semibold tabular-nums ' +
            (over ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white')
          }
        >
          {value.toFixed(2)}%
        </span>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${over ? 'bg-red-500' : 'bg-emerald-500'}`}
          style={{ width: `${fillPercent}%` }}
        />
      </div>
      <div className="mt-1 flex items-center justify-between text-[10px] text-slate-400 dark:text-slate-500">
        <span>Limit {max.toFixed(2)}%</span>
        {over && <span className="font-medium text-red-500 dark:text-red-400">Over limit</span>}
      </div>
    </div>
  )
}
